import React, { useEffect, useRef, useCallback } from "react";
import { Html5Qrcode } from "html5-qrcode";
import "./style/qrScanner.scss";
import { useStrings } from "../../../hooks/useStrings";

interface QRScannerProps {
  isActive: boolean;
  onCodeResult: (decodedText: string) => void;
  onPermissionChange?: (hasPermission: boolean) => void;
  onError?: (message: string) => void;
  onCameraError?: (info: {
    kind: "denied" | "busy" | "notfound" | "init";
    raw?: DOMException;
  }) => void;
}

export const QRScanner: React.FC<QRScannerProps> = ({
  isActive,
  onCodeResult,
  onPermissionChange,
  onError,
  onCameraError,
}) => {
  const S = useStrings();
  const invalidQRMsg = S.QR_SCANNER.INVALID_CODE_ERROR;
  const scannerInstanceRef = useRef<Html5Qrcode | null>(null);
  const isStartingRef = useRef<boolean>(false);
  const isMountedRef = useRef<boolean>(true);

  const clearScanner = useCallback(async () => {
    isStartingRef.current = false;
    const scanner = scannerInstanceRef.current;

    if (!scanner) return;

    try {
      const state = scanner.getState();
      // 2 = SCANNING, 3 = PAUSED
      if (state === 2 || state === 3) {
        await scanner.stop();
      }
    } catch {
      // Ignore stop errors - scanner may not be running
    }

    try {
      scanner.clear();
    } catch {
      // Ignore clear errors - DOM may already be cleaned up
    }

    // Stop all video tracks to release camera
    try {
      const video = document.querySelector(
        "#qr-reader video",
      ) as HTMLVideoElement | null;
      if (video?.srcObject instanceof MediaStream) {
        video.srcObject.getTracks().forEach((track) => track.stop());
      }
    } catch {
      // Ignore errors
    }

    scannerInstanceRef.current = null;
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isActive) return;
    if (isStartingRef.current) return;

    const startScanner = async () => {
      const container = document.getElementById("qr-reader");
      if (!container) {
        console.error("QR reader container not found");
        return;
      }

      if (isStartingRef.current) return;
      isStartingRef.current = true;

      try {
        scannerInstanceRef.current = new Html5Qrcode("qr-reader");

        await scannerInstanceRef.current.start(
          { facingMode: "environment" },
          {
            fps: 10,
          },
          async (decodedText) => {
            if (!decodedText || decodedText.trim().length < 5) {
              console.debug("QR scan result too short:", decodedText);
              onError?.(invalidQRMsg);
              return;
            }

            try {
              new URL(decodedText);
              // Stop scanner before calling callback
              await clearScanner();
              if (isMountedRef.current) {
                onCodeResult(decodedText);
              }
            } catch {
              console.debug("QR scan result is not a valid URL:", decodedText);
              onError?.(invalidQRMsg);
            }
          },
          () => {
            // Called for every frame without a readable QR ("No MultiFormat
            // Readers were able to detect the code"), ~10x/sec while the camera
            // is open. Not an error; logging it flooded Middleware (EN-5047).
          },
        );

        if (isMountedRef.current) {
          onPermissionChange?.(true);
        }
      } catch (err) {
        console.error("Camera failed to start:", err);
        if (isMountedRef.current) {
          // html5-qrcode does NOT reject with the raw getUserMedia
          // DOMException — it wraps it in a plain Error whose message embeds
          // the original name, e.g. "Error getting userMedia, error =
          // NotReadableError: ...". So classify by scanning a haystack of the
          // DOMException name (if any) plus the error message/string.
          const raw = err instanceof DOMException ? err : undefined;
          const message =
            err instanceof Error ? err.message : String(err ?? "");
          const haystack = `${raw?.name ?? ""} ${message}`;
          const has = (token: string) => haystack.includes(token);

          if (has("NotAllowedError") || has("PermissionDeniedError")) {
            onPermissionChange?.(false);
            onCameraError?.({ kind: "denied", raw });
          } else if (has("NotReadableError") || has("TrackStartError")) {
            onCameraError?.({ kind: "busy", raw });
          } else if (has("NotFoundError") || has("DevicesNotFoundError")) {
            onCameraError?.({ kind: "notfound", raw });
          } else {
            // Includes SecurityError and otherwise-unclassified failures
            onCameraError?.({ kind: "init", raw });
          }
        }
      }
    };

    const timeoutId: ReturnType<typeof setTimeout> = setTimeout(
      startScanner,
      100,
    );

    return () => {
      clearTimeout(timeoutId);
      clearScanner();
    };
  }, [isActive, onCodeResult, onPermissionChange, onError, onCameraError, clearScanner, invalidQRMsg]);

  return (
    <div
      id="qr-reader"
      key="qr-reader-node"
      // style={{ width: "100%", minHeight: 200 }}
    />
  );
};

export default QRScanner;
